import React, { useState, useEffect, createContext, useContext } from 'react';
import TourCard from './TourCard';
import { tours as mockTours } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create a context to share tours data across components
export const ToursContext = createContext(null);

export const ToursProvider = ({ children }) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        // First try to seed the database
        await axios.post(`${BACKEND_URL}/api/seed-tours`);
        // Then fetch tours
        const response = await axios.get(`${BACKEND_URL}/api/tours`);
        if (response.data && response.data.length > 0) {
          setTours(response.data);
        } else {
          // Fallback to mock if API returns empty
          setTours(mockTours);
        }
      } catch (err) {
        console.log('Using mock data due to API error');
        setTours(mockTours);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  const refreshTours = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tours`);
      if (response.data && response.data.length > 0) {
        setTours(response.data);
      }
    } catch (err) {
      console.error('Error refreshing tours:', err);
    }
  };

  return (
    <ToursContext.Provider value={{ tours, loading, error, refreshTours }}>
      {children}
    </ToursContext.Provider>
  );
};

export const useTours = () => {
  const context = useContext(ToursContext);
  if (!context) {
    // Return mock data if context is not available
    return { tours: mockTours, loading: false, error: null, refreshTours: () => {} };
  }
  return context;
};

const ToursSection = () => {
  const { t } = useLanguage();
  const { tours, loading } = useTours();

  if (loading) {
    return (
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando tours...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-gray-50" id="excursiones">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-teal-500 mb-4">
          {t('toursTitle')}
        </h2>
        <p className="text-center text-gray-600 mb-12">{t('toursSubtitle')}</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ToursSection;
